import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import StickyPageHeader from '../common/StickyPageHeader';
import {
  Plus,
  Edit2,
  Trash2,
  Search,
  Filter,
  SlidersHorizontal,
  ArrowUpRight,
  ArrowDownRight,
  ArrowLeftRight,
  ArrowRight,
  Calendar,
  CalendarDays,
  CalendarRange,
  Settings2,
  X,
  Link2,
  Layers,
  Building2,
  Eye,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useConfig } from '../../context/ConfigContext';
import { useTheme } from '../../context/ThemeContext';
import { useTransactionsQuery, useDeleteTransaction, useAccountsQuery, useCategoriesQuery } from '../../hooks/useQueries';
import { getCategoryLabel, formatDate as formatDateInput } from '../../utils/formatters';
import { fetchMediaAsBlob } from '../../services/api';
import ConfirmModal from '../common/ConfirmModal';
import Pagination from '../common/Pagination';
import AttachmentViewerModal from '../common/AttachmentViewerModal';
import MobileSelect from '../common/MobileSelect';
import useModalBack from '../../hooks/useModalBack';
import DateTimeInput from '../common/DateTimeInput';
import { TransactionListSkeleton } from '../common/Skeleton';
import { getIconComponent } from '../common/IconPicker';

// Filter type options with icons and colors
const FILTER_TYPE_OPTIONS = [
  { value: '', label: 'All', icon: Layers, iconColor: '#6b7280' },
  { value: 'income', label: 'Income', icon: ArrowUpRight, iconColor: '#22c55e' },
  { value: 'expense', label: 'Expense', icon: ArrowDownRight, iconColor: '#ef4444' },
  { value: 'transfer', label: 'Transfer', icon: ArrowLeftRight, iconColor: '#6366f1' }
];

const PERIOD_OPTIONS = [
  { value: 'thisMonth', label: 'This Month', icon: CalendarDays, iconColor: '#8b5cf6' },
  { value: 'lastMonth', label: 'Last Month', icon: Calendar, iconColor: '#3b82f6' },
  { value: 'annually', label: 'This Year', icon: CalendarRange, iconColor: '#22c55e' },
  { value: 'custom', label: 'Custom', icon: Settings2, iconColor: '#6b7280' }
];

// Format date as YYYY-MM-DD in local timezone (not UTC)
function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Compute date range from period preset
function getDateRangeFromPeriod(period) {
  const now = new Date();
  let start, end;
  if (period === 'thisMonth') {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
    end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  } else if (period === 'lastMonth') {
    start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    end = new Date(now.getFullYear(), now.getMonth(), 0);
  } else if (period === 'annually') {
    start = new Date(now.getFullYear(), 0, 1); // Jan 1
    end = new Date(now.getFullYear(), 11, 31); // Dec 31
  }
  if (start && end) {
    return {
      startDate: formatLocalDate(start),
      endDate: formatLocalDate(end)
    };
  }
  return { startDate: '', endDate: '' };
}

export default function TransactionList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isTaxUser = user?.email === import.meta.env.VITE_TAX_ALLOWED_EMAIL;
  const { toast } = useToast();
  const { formatAmount, formatDate, formatTime, config } = useConfig();
  const { isModernUI } = useTheme();

  // Get initial values from URL params
  const urlAccount = searchParams.get('account') || '';
  const urlPeriod = searchParams.get('period') || 'thisMonth';
  const urlType = searchParams.get('type') || '';
  const urlSearch = searchParams.get('search') || '';

  // Compute initial dates based on URL period
  const getInitialDates = () => {
    const p = urlPeriod || 'thisMonth';
    if (p === 'custom') {
      return {
        startDate: searchParams.get('startDate') || '',
        endDate: searchParams.get('endDate') || ''
      };
    }
    return getDateRangeFromPeriod(p);
  };
  const initialDates = getInitialDates();

  const [searchTerm, setSearchTerm] = useState(urlSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(urlSearch);
  const [filterType, setFilterType] = useState(urlType);
  const [filterAccountName, setFilterAccountName] = useState(urlAccount); // Store account name from URL
  const [filterAccount, setFilterAccount] = useState(''); // Store account ID for internal use
  const [period, setPeriod] = useState(urlPeriod);
  const [startDate, setStartDate] = useState(initialDates.startDate);
  const [endDate, setEndDate] = useState(initialDates.endDate);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);

  const [autoOpenEndDate, setAutoOpenEndDate] = useState(false);

  // Temp/draft filter state for modal (only applied on "Apply Filters" click)
  const [tempFilterType, setTempFilterType] = useState(filterType);
  const [tempFilterAccount, setTempFilterAccount] = useState('');
  const [tempFilterAccountName, setTempFilterAccountName] = useState(filterAccountName);
  const [tempPeriod, setTempPeriod] = useState(period);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);

  const closeFilterModal = useCallback(() => setShowFilterModal(false), []);
  const openFilterModal = useCallback(() => {
    // Sync temp state with current filters when opening
    setTempFilterType(filterType);
    setTempFilterAccount(filterAccount);
    setTempFilterAccountName(filterAccountName);
    setTempPeriod(period);
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setShowFilterModal(true);
  }, [filterType, filterAccount, filterAccountName, period, startDate, endDate]);
  useModalBack(showFilterModal, closeFilterModal, 'TransactionFilter');

  // Handle temp period change - auto-compute dates in draft
  const handleTempPeriodChange = useCallback((newPeriod) => {
    setTempPeriod(newPeriod);
    if (newPeriod !== 'custom') {
      const { startDate: s, endDate: e } = getDateRangeFromPeriod(newPeriod);
      setTempStartDate(s);
      setTempEndDate(e);
    }
  }, []);

  // Apply filters from draft to actual state (mobile bottom sheet)
  // Sync filters to URL params so they persist across navigation
  const syncFiltersToURL = useCallback((updates = {}) => {
    const newParams = new URLSearchParams(searchParams);
    const p = updates.period ?? period;
    const t = updates.filterType ?? filterType;
    const a = updates.filterAccountName ?? filterAccountName;

    if (p && p !== 'thisMonth') newParams.set('period', p);
    else newParams.delete('period');
    if (t) newParams.set('type', t);
    else newParams.delete('type');
    if (a) newParams.set('account', a);
    else newParams.delete('account');
    if (p === 'custom') {
      if (updates.startDate ?? startDate) newParams.set('startDate', updates.startDate ?? startDate);
      if (updates.endDate ?? endDate) newParams.set('endDate', updates.endDate ?? endDate);
    } else {
      newParams.delete('startDate');
      newParams.delete('endDate');
    }
    setSearchParams(newParams, { replace: true });
  }, [searchParams, period, filterType, filterAccountName, startDate, endDate, setSearchParams]);

  const applyFilters = useCallback(() => {
    setFilterType(tempFilterType);
    setFilterAccount(tempFilterAccount);
    setFilterAccountName(tempFilterAccountName);
    setPeriod(tempPeriod);
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setCurrentPage(1);
    setShowFilterModal(false);
    // Sync to URL so filters persist after navigation
    setTimeout(() => syncFiltersToURL({
      period: tempPeriod,
      filterType: tempFilterType,
      filterAccountName: tempFilterAccountName,
      startDate: tempStartDate,
      endDate: tempEndDate
    }), 0);
  }, [tempFilterType, tempFilterAccount, tempFilterAccountName, tempPeriod, tempStartDate, tempEndDate, syncFiltersToURL]);

  // Handle period change directly (desktop filters)
  const handlePeriodChange = useCallback((newPeriod) => {
    setPeriod(newPeriod);
    setTempPeriod(newPeriod);
    if (newPeriod !== 'custom') {
      const { startDate: s, endDate: e } = getDateRangeFromPeriod(newPeriod);
      setStartDate(s);
      setEndDate(e);
      setTempStartDate(s);
      setTempEndDate(e);
    }
    setCurrentPage(1);
    syncFiltersToURL({ period: newPeriod });
  }, [syncFiltersToURL]);

  // Attachment viewer state
  const [attachmentViewer, setAttachmentViewer] = useState({
    isOpen: false,
    src: null,
    mimeType: null,
    filename: null,
    loading: false
  });

  const hasDateFilter = period !== 'thisMonth' || filterType || filterAccount || filterAccountName || debouncedSearch;

  // Fetch accounts for filter
  const { data: accounts = [] } = useAccountsQuery();

  // Sync filterAccount (ID) with filterAccountName when accounts load
  useEffect(() => {
    if (filterAccountName && accounts.length > 0 && !filterAccount) {
      const matchedAccount = accounts.find(a => a.name === filterAccountName);
      if (matchedAccount) {
        setFilterAccount(matchedAccount._id);
      }
    }
  }, [filterAccountName, accounts, filterAccount]);

  // Fetch categories for icons
  const { data: categories = [] } = useCategoriesQuery();



  // Helper to get category info (icon, color)
  const getCategoryInfo = (categoryName, type) => {
    const category = categories.find(c => c.name === categoryName && c.type === type);
    if (category) {
      return {
        icon: getIconComponent(category.icon),
        color: category.color || (type === 'income' ? '#22c55e' : '#ef4444')
      };
    }
    // Fallback for transfer or unknown
    return {
      icon: type === 'transfer' ? ArrowLeftRight : (type === 'income' ? ArrowUpRight : ArrowDownRight),
      color: type === 'transfer' ? '#6366f1' : (type === 'income' ? '#22c55e' : '#ef4444')
    };
  };

  // Build account filter options
  const accountFilterOptions = useMemo(() => {
    const options = [
      { value: '', label: 'All', icon: Building2, iconColor: '#6b7280' }
    ];
    accounts.filter(a => a.isActive !== false).forEach(account => {
      options.push({
        value: account._id,
        label: account.name,
        icon: Building2,
        iconColor: account.color || '#6b7280'
      });
    });
    return options;
  }, [accounts]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Query params for React Query - compute account name directly here
  const queryParams = useMemo(() => {
    // Get account name from ID (server stores account names, not IDs)
    // Or use filterAccountName directly if set from URL
    let accountName = filterAccountName || undefined;
    if (!accountName && filterAccount && accounts.length > 0) {
      const account = accounts.find(a => a._id === filterAccount);
      accountName = account?.name;
    }

    return {
      page: currentPage,
      limit: 100,
      type: filterType || undefined,
      account: accountName,
      financialYear: user?.financialYear,
      search: debouncedSearch || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      sortBy: 'date',
      sortOrder: 'desc'
    };
  }, [currentPage, filterType, filterAccount, filterAccountName, accounts, user?.financialYear, debouncedSearch, startDate, endDate]);

  // React Query hooks
  const { data, isLoading } = useTransactionsQuery(queryParams);
  const deleteTransactionMutation = useDeleteTransaction();

  const transactions = data?.transactions?.filter(Boolean) || [];
  const pagination = data?.pagination || { page: 1, limit: 100, total: 0, pages: 0 };

  // Calculate totals for current page (excluding transfers as they're internal movements)
  const totals = transactions.reduce(
    (acc, t) => {
      if (!t) return acc;
      if (t.type === 'income') {
        acc.credit += (t.amountInINR || 0) - (isTaxUser ? (t.tdsAmount || 0) : 0);
        acc.tds += isTaxUser ? (t.tdsAmount || 0) : 0;
      } else if (t.type === 'expense') {
        acc.debit += t.amountInINR || 0;
      }
      return acc;
    },
    { credit: 0, debit: 0, tds: 0 }
  );

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const handleEdit = (transaction) => {
    navigate(`/transactions/${transaction._id}/edit`);
  };

  const handleViewDetails = (transaction) => {
    navigate(`/transactions/${transaction._id}`);
  };

  const handleDeleteClick = (id) => {
    setDeleteConfirm({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirm.id) {
      const deletingTransaction = transactions.find(t => t?._id === deleteConfirm.id);
      const isTransfer = deletingTransaction?.type === 'transfer';

      try {
        await deleteTransactionMutation.mutateAsync(deleteConfirm.id);
        toast.success(isTransfer ? 'Transfer deleted successfully' : 'Transaction deleted successfully');

        // If deleting would leave the page empty and it's not page 1, go to previous page
        const remainingItems = transactions.length - 1;
        if (remainingItems <= 0 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }
      } catch (error) {
        toast.error('Failed to delete transaction');
      }
    }
    setDeleteConfirm({ open: false, id: null });
  };

  const handleAddNew = () => {
    navigate('/transactions/new');
  };

  // Handle viewing attachment
  const handleViewAttachment = async (transaction) => {
    if (!transaction.attachment?.url) return;

    setAttachmentViewer(prev => ({ ...prev, loading: true }));

    try {
      const mimeType = transaction.attachment.mimeType;

      // Pass MIME type to ensure blob is created with correct type
      const mediaUrl = await fetchMediaAsBlob(transaction.attachment.url, mimeType);

      setAttachmentViewer({
        isOpen: true,
        src: mediaUrl,
        mimeType: mimeType,
        filename: transaction.attachment.filename || 'attachment',
        loading: false
      });
    } catch (error) {
      console.error('Failed to load attachment:', error);
      toast.error('Failed to load attachment');
      setAttachmentViewer(prev => ({ ...prev, loading: false }));
    }
  };

  // Close attachment viewer and cleanup blob URL
  const closeAttachmentViewer = () => {
    if (attachmentViewer.src) {
      URL.revokeObjectURL(attachmentViewer.src);
    }
    setAttachmentViewer({
      isOpen: false,
      src: null,
      mimeType: null,
      filename: null,
      loading: false
    });
  };

  // Show skeleton on initial load (no cached data)
  if (isLoading && transactions.length === 0) {
    return <TransactionListSkeleton />;
  }

  return (
    <div className="space-y-3 md:space-y-4">
      {/* Mobile Header with Filter */}
      <StickyPageHeader className={isModernUI ? "xl:hidden flex items-center justify-between mb-2" : "xl:hidden flex items-center justify-between mb-3"}>
        <div>
          <h1 className={isModernUI ? "text-base font-bold text-surface-900" : "text-lg font-semibold text-surface-900"}>Transactions</h1>
          <p className={isModernUI ? "text-[10px] text-surface-500" : "text-xs text-surface-500"}>{pagination.total} transactions</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={openFilterModal}
            className={`relative w-9 h-9 rounded-xl flex items-center justify-center border transition-all ${
              (filterType || filterAccount || filterAccountName || period !== 'thisMonth' || debouncedSearch)
                ? 'bg-primary-50 border-primary-200 text-primary-600'
                : 'bg-white border-surface-200 text-surface-600'
            }`}
          >
            <SlidersHorizontal className="w-4 h-4" />
            {(filterType || filterAccount || filterAccountName || period !== 'thisMonth' || debouncedSearch) && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary-500 text-white text-[9px] font-bold flex items-center justify-center">
                {[filterType, filterAccount || filterAccountName, period !== 'thisMonth' ? period : null, debouncedSearch].filter(Boolean).length}
              </span>
            )}
          </button>
        </div>
      </StickyPageHeader>

      {/* Mobile Filter Bottom Sheet */}
      {showFilterModal && createPortal(
        <div className="xl:hidden fixed inset-0 z-50 flex items-end !mt-0" onClick={closeFilterModal}>
          {/* Overlay */}
          <div className="absolute inset-0 bg-black/40" />

          {/* Bottom Sheet */}
          <div
            className="relative w-full bg-white rounded-t-2xl animate-slide-up max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-surface-300" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-4 pb-3">
              <h2 className="text-[15px] font-bold text-surface-900">Filter Transactions</h2>
              <div className="flex items-center gap-2">
                {(tempFilterType || tempFilterAccount || tempFilterAccountName || tempPeriod !== 'thisMonth' || searchTerm) && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setTempFilterType('');
                      setTempFilterAccount('');
                      setTempFilterAccountName('');
                      setTempPeriod('thisMonth');
                      const { startDate: s, endDate: e } = getDateRangeFromPeriod('thisMonth');
                      setTempStartDate(s);
                      setTempEndDate(e);
                    }}
                    className="text-xs font-semibold px-2 py-1"
                    style={{ color: 'var(--accent-bg)' }}
                  >
                    Clear All
                  </button>
                )}
                <button
                  onClick={closeFilterModal}
                  className="w-8 h-8 rounded-lg bg-surface-100 flex items-center justify-center text-surface-500"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="overflow-y-auto px-4 pb-6 space-y-4">
              {/* Search */}
              <div>
                <label className="block text-[11px] font-semibold text-surface-400 uppercase tracking-wider mb-2">Search</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm">🔍</span>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by description..."
                    className="w-full h-11 pl-10 pr-10 text-sm bg-white border border-surface-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-surface-400 hover:text-surface-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Type Filter */}
              <div>
                <label className="block text-[11px] font-semibold text-surface-400 uppercase tracking-wider mb-2">Transaction Type</label>
                <div className="grid grid-cols-4 gap-2">
                  {FILTER_TYPE_OPTIONS.map((option) => {
                    const Icon = option.icon;
                    const isSelected = tempFilterType === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() => setTempFilterType(option.value)}
                        className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all ${
                          isSelected
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-surface-200 bg-white hover:border-surface-300'
                        }`}
                      >
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${option.iconColor}15` }}
                        >
                          <Icon className="w-4 h-4" style={{ color: option.iconColor }} />
                        </div>
                        <span className={`text-xs font-medium ${isSelected ? 'text-primary-700' : 'text-surface-600'}`}>
                          {option.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Account Filter */}
              <div>
                <label className="block text-[11px] font-semibold text-surface-400 uppercase tracking-wider mb-2">Account</label>
                <MobileSelect
                  value={tempFilterAccount}
                  onChange={(value) => {
                    setTempFilterAccount(value);
                    setTempFilterAccountName('');
                  }}
                  options={accountFilterOptions}
                  placeholder="All Accounts"
                  label="Select Account"
                  columns={2}
                />
              </div>

              {/* Time Period */}
              <div>
                <label className="block text-[11px] font-semibold text-surface-400 uppercase tracking-wider mb-2">Time Period</label>
                <div className="grid grid-cols-2 gap-2">
                  {PERIOD_OPTIONS.map((option) => {
                    const Icon = option.icon;
                    const isSelected = tempPeriod === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() => handleTempPeriodChange(option.value)}
                        className={`flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all ${
                          isSelected
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-surface-200 bg-white hover:border-surface-300'
                        }`}
                      >
                        <div
                          className="w-7 h-7 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${option.iconColor}15` }}
                        >
                          <Icon className="w-3.5 h-3.5" style={{ color: option.iconColor }} />
                        </div>
                        <span className={`text-sm font-medium ${isSelected ? 'text-primary-700' : 'text-surface-600'}`}>
                          {option.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Custom Date Range */}
              {tempPeriod === 'custom' && (
                <div>
                  <div className="flex gap-2 items-center">
                    <div className="flex-1">
                      <DateTimeInput
                        dateOnly
                        value={tempStartDate}
                        onChange={(date) => setTempStartDate(date)}
                        placeholder="From"
                        maxDate={tempEndDate || undefined}
                      />
                    </div>
                    <span className="text-surface-300 text-sm">&rarr;</span>
                    <div className="flex-1">
                      <DateTimeInput
                        dateOnly
                        value={tempEndDate}
                        onChange={(date) => setTempEndDate(date)}
                        placeholder="To"
                        minDate={tempStartDate || undefined}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Apply Button */}
              <button
                onClick={applyFilters}
                className="w-full py-3.5 rounded-xl font-semibold text-white transition-all shadow-lg"
                style={{ backgroundColor: 'var(--accent-bg)' }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Desktop Header */}
      <div className="hidden xl:flex xl:items-center xl:justify-between gap-4">
        <div className="page-header mb-0">
          <h1 className="page-title">Transactions</h1>
          <p className="page-subtitle">
            {pagination.total} transactions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleAddNew}
            className="inline-flex items-center justify-center px-5 py-3 rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all"
            style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Transaction
          </button>
        </div>
      </div>

      {/* Desktop Filters */}
      <div className="hidden xl:block card">
        <div className="flex items-center gap-3">
          {/* Search */}
          <form onSubmit={handleSearch} className="w-64">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm">🔍</span>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="input pl-10"
              />
            </div>
          </form>

          {/* Type Filter */}
          <select
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setCurrentPage(1);
              syncFiltersToURL({ filterType: e.target.value });
            }}
            className="select w-36"
          >
            <option value="">All Types</option>
            <option value="income">Income</option>
            <option value="expense">Expense</option>
            <option value="transfer">Transfer</option>
          </select>

          {/* Account Filter */}
          <select
            value={filterAccount}
            onChange={(e) => {
              setFilterAccount(e.target.value);
              setFilterAccountName('');
              setCurrentPage(1);
              syncFiltersToURL({ filterAccountName: '' });
            }}
            className="select w-40"
          >
            <option value="">All Accounts</option>
            {accounts.filter(a => a.isActive !== false).map(account => (
              <option key={account._id} value={account._id}>
                {account.name}
              </option>
            ))}
          </select>

          {/* Period Filter */}
          <div className="relative">
            <select
              value={period}
              onChange={(e) => handlePeriodChange(e.target.value)}
              className="select w-40"
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Custom Date Filters — inline next to period dropdown */}
          {period === 'custom' && (
            <>
              <DateTimeInput
                dateOnly
                value={startDate}
                onChange={(date) => {
                  setStartDate(date);
                  // Reset endDate if it's before the new startDate
                  if (endDate && date > endDate) setEndDate('');
                  setCurrentPage(1);
                }}
                placeholder="From"
              />
              <span className="text-surface-400 text-sm">to</span>
              <DateTimeInput
                dateOnly
                value={endDate}
                onChange={(date) => { setEndDate(date); setCurrentPage(1); }}
                placeholder="To"
                minDate={startDate || undefined}
              />
            </>
          )}

          {/* Clear Filters — desktop */}
          {(filterType || filterAccount || filterAccountName || period !== 'thisMonth' || debouncedSearch) && (
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterType('');
                setFilterAccount('');
                setFilterAccountName('');
                handlePeriodChange('thisMonth');
                setCurrentPage(1);
                setSearchParams({});
              }}
              className="px-3 py-2 text-sm text-surface-500 hover:text-surface-700 hover:bg-surface-100 rounded-xl transition-colors flex-shrink-0"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Empty State - only show if not loading and no data */}
      {!isLoading && transactions.length === 0 ? (
        /* Empty State */
        <div className="card text-center py-12">
          <div className="w-16 h-16 bg-surface-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Filter className="w-8 h-8 text-surface-400" />
          </div>
          <h3 className="text-lg font-medium text-surface-900 mb-2">
            No transactions found
          </h3>
          <p className="text-surface-500 mb-4">
            {searchTerm || filterType || filterAccount
              ? 'Try adjusting your filters'
              : 'Start by adding your first transaction'}
          </p>
          <button
            onClick={handleAddNew}
            className="btn-primary inline-flex"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Transaction
          </button>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden xl:block card overflow-hidden p-0">
            <table className="w-full table-fixed">
              <thead>
                <tr className="table-header border-b border-surface-200">
                  <th className="table-cell w-28">Date</th>
                  <th className="table-cell">Description</th>
                  <th className="table-cell w-32">Bank</th>
                  <th className="table-cell w-32">Category</th>
                  <th className="table-cell text-right w-32">Credit (+)</th>
                  <th className="table-cell text-right w-32">Debit (-)</th>
                  <th className="table-cell text-right w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-100">
                {transactions.map((transaction) => (
                  <tr
                    key={transaction._id}
                    className="hover:bg-surface-50 transition-colors"
                  >
                    <td className="table-cell whitespace-nowrap">
                      <div className="flex flex-col">
                        <span>{formatDate(transaction.date)}</span>
                        {transaction.time && (
                          <span className="text-xs text-surface-400">
                            {formatTime(transaction.time)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="table-cell font-medium text-surface-900 break-words">
                      <div className="flex items-center gap-1.5">
                        <span className="flex-1">{transaction.description}</span>
                        {transaction.attachment?.url && (
                          <button
                            onClick={() => handleViewAttachment(transaction)}
                            className="p-1 text-primary-500 hover:text-primary-700 hover:bg-primary-50 rounded transition-colors flex-shrink-0"
                            title="View attachment"
                          >
                            <Link2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="table-cell text-surface-600 truncate">
                      {transaction.type === 'transfer' && (transaction.toAccount?.name || transaction.toAccount)
                        ? `${transaction.account?.name || transaction.bankName} → ${(transaction.toAccount?.name || transaction.toAccount)}`
                        : transaction.account?.name || transaction.bankName}
                    </td>
                    <td className="table-cell">
                      <span
                        className={
                          transaction.type === 'income'
                            ? 'badge-income'
                            : transaction.type === 'transfer'
                            ? 'badge-transfer'
                            : 'badge-expense'
                        }
                      >
                        {transaction.type === 'transfer' ? 'Transfer' : getCategoryLabel(transaction.category?.name || transaction.category)}
                      </span>
                    </td>
                    <td className="table-cell text-right whitespace-nowrap">
                      {transaction.type === 'income' && (
                        <div>
                          <span className="font-medium" style={{ color: config.incomeColor }}>
                            {formatAmount(transaction.amountInINR - (isTaxUser ? (transaction.tdsAmount || 0) : 0))}
                          </span>
                          {isTaxUser && transaction.tdsAmount > 0 && (
                            <>
                              <p className="text-xs text-surface-400">
                                TDS: {formatAmount(transaction.tdsAmount)}
                              </p>
                              <p className="text-xs text-surface-500">
                                Total: {formatAmount(transaction.amountInINR)}
                              </p>
                            </>
                          )}
                        </div>
                      )}
                      {transaction.type === 'transfer' && (
                        <span className="text-primary-600 font-medium">
                          {formatAmount(transaction.amountInINR)}
                        </span>
                      )}
                    </td>
                    <td className="table-cell text-right whitespace-nowrap">
                      {transaction.type === 'expense' && (
                        <span className="font-medium" style={{ color: config.expenseColor }}>
                          {formatAmount(transaction.amountInINR)}
                        </span>
                      )}
                    </td>
                    <td className="table-cell text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleEdit(transaction)}
                          className="p-2 text-primary-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(transaction._id)}
                          className="p-2 text-expense-500 hover:text-expense-600 hover:bg-expense-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              {/* Totals Footer */}
              <tfoot className="bg-surface-50 border-t-2 border-surface-200">
                <tr>
                  <td colSpan="4" className="table-cell font-semibold text-surface-900">
                    Page Total
                  </td>
                  <td className="table-cell text-right font-semibold">
                    <span style={{ color: config.incomeColor }}>{formatAmount(totals.credit)}</span>
                    {isTaxUser && totals.tds > 0 && (
                      <>
                        <p className="text-xs text-surface-400">TDS: {formatAmount(totals.tds)}</p>
                        <p className="text-xs text-surface-500">Total: {formatAmount(totals.credit + totals.tds)}</p>
                      </>
                    )}
                  </td>
                  <td className="table-cell text-right font-semibold">
                    <span style={{ color: config.expenseColor }}>{formatAmount(totals.debit)}</span>
                  </td>
                  <td className="table-cell"></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Mobile Cards View - Grouped by Date */}
          <div className="xl:hidden space-y-3">
            {(() => {
              // Group transactions by date
              const groupedTransactions = transactions.reduce((groups, transaction) => {
                // Parse date - convert to local date for proper grouping
                let dateKey = '';
                if (transaction.date) {
                  // Parse as Date object to get local date
                  const dateObj = new Date(transaction.date);
                  // Format as YYYY-MM-DD using local timezone
                  const year = dateObj.getFullYear();
                  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                  const day = String(dateObj.getDate()).padStart(2, '0');
                  dateKey = `${year}-${month}-${day}`;
                }
                if (!groups[dateKey]) {
                  groups[dateKey] = [];
                }
                groups[dateKey].push(transaction);
                return groups;
              }, {});

              // Sort date keys in descending order (newest first)
              const sortedDateKeys = Object.keys(groupedTransactions).sort((a, b) => {
                return new Date(b) - new Date(a);
              });

              // Get display label for date
              const getDateLabel = (dateStr) => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const yesterday = new Date(today);
                yesterday.setDate(yesterday.getDate() - 1);

                const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                const yesterdayStr = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;

                if (dateStr === todayStr) return 'Today';
                if (dateStr === yesterdayStr) return 'Yesterday';
                return formatDate(dateStr);
              };

              return sortedDateKeys.map((dateKey) => {
                const dateTransactions = groupedTransactions[dateKey];
                return (
                <div key={dateKey}>
                  {/* Date Header */}
                  <div className="px-1 py-1.5">
                    <span className="text-[11px] font-bold text-surface-400 uppercase tracking-wider">
                      {getDateLabel(dateKey)}
                    </span>
                  </div>

                  {/* Transactions for this date */}
                  <div className="bg-white rounded-2xl border border-surface-100 overflow-hidden divide-y divide-surface-50">
                    {dateTransactions.map((transaction) => {
                      const categoryInfo = getCategoryInfo(transaction.category?.name || transaction.category, transaction.type);
                      const CategoryIcon = categoryInfo.icon;
                      const isIncome = transaction.type === 'income';
                      const isTransfer = transaction.type === 'transfer';
                      const displayAmount = isIncome && isTaxUser
                        ? transaction.amountInINR - (transaction.tdsAmount || 0)
                        : transaction.amountInINR;
                      const amountColor = isIncome ? config.incomeColor : isTransfer ? 'var(--accent-bg)' : config.expenseColor;
                      const categoryLabel = isTransfer ? 'Transfer' : getCategoryLabel(transaction.category?.name || transaction.category);

                      return (
                        <div
                          key={transaction._id}
                          className="flex items-center gap-3 px-3 py-3 active:bg-surface-50 transition-colors cursor-pointer"
                          onClick={() => handleViewDetails(transaction)}
                        >
                          {/* Category Icon */}
                          <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${categoryInfo.color}12` }}
                          >
                            <CategoryIcon className="w-5 h-5" style={{ color: categoryInfo.color }} />
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-surface-900 text-[14px] leading-tight line-clamp-1">
                              {transaction.description}
                            </p>
                            <p className="text-[11px] text-surface-400 mt-0.5">
                              {transaction.time && `${formatTime(transaction.time)} \u00b7 `}{isTransfer && (transaction.toAccount?.name || transaction.toAccount) ? `${transaction.account?.name || transaction.bankName} \u2192 ${(transaction.toAccount?.name || transaction.toAccount)}` : transaction.account?.name || transaction.bankName}
                            </p>
                          </div>

                          {/* Amount + Category */}
                          <div className="text-right flex-shrink-0 pl-2">
                            <p className="font-extrabold text-[15px]" style={{ color: amountColor }}>
                              {isIncome ? '+' : isTransfer ? '' : '-'}{formatAmount(displayAmount)}
                            </p>
                            {categoryLabel && (
                              <p className="text-[10px] text-surface-400 mt-0.5 truncate max-w-[90px]">{categoryLabel}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                );
              });
            })()}

            {/* Spacer for FAB */}
            <div className="h-20" />
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.pages}
            totalItems={pagination.total}
            itemsPerPage={pagination.limit}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {/* FAB for Mobile */}
      <button
        onClick={handleAddNew}
        className="fab"
        style={{ opacity: 0.85 }}
        aria-label="Add transaction"
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, id: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Transaction"
        message="Are you sure you want to delete this transaction? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
      />

      {/* Attachment Viewer Modal */}
      <AttachmentViewerModal
        isOpen={attachmentViewer.isOpen}
        onClose={closeAttachmentViewer}
        src={attachmentViewer.src}
        mimeType={attachmentViewer.mimeType}
        filename={attachmentViewer.filename}
      />
    </div>
  );
}
